/**
 * Role-based navigation definitions — single source for desktop sidebar and mobile drawer.
 * RoleSidebar / buildNavSections render the same links at every viewport; only layout differs.
 */
import { FEATURE_AI_SURVEY_UI_ENABLED } from '../config/features';
import {
  canManageSkillCatalog,
  hasAdminLikeAccess,
  isAdmin,
  isDhOnlyRestricted,
  isTruthyFlag,
  shouldShowAdminPersonalTimesheetNav,
} from '../utils/userAccess';
import {
  ROLE_CODES,
  resolveAssignedRoles,
  canAccessMySkillMatrix,
  canAccessResourceFinder,
  canAccessBenchManagement,
  canAccessBenchAnalysis,
  canAccessTeamResumes,
} from '../utils/rolePriority';

/** @typedef {'link' | 'action'} NavItemType */

/**
 * @typedef {Object} NavContext
 * @property {boolean} rmMenuAccess
 * @property {boolean} showCsgNav
 * @property {boolean} pmAndCsg
 * @property {boolean} dhRestricted
 * @property {boolean} rmOnlyNav
 */

/**
 * @param {object|null|undefined} user
 * @returns {NavContext}
 */
export function buildNavContext(user) {
  const rmMenuAccess = !!user?.isReportingManager;
  const assignedRoles = resolveAssignedRoles(user);
  const showCsgNav =
    assignedRoles.includes(ROLE_CODES.CSG) || isTruthyFlag(user?.isCsg);
  const pmAndCsg = !!(user?.isProjectManager && showCsgNav);
  const dhRestricted = isDhOnlyRestricted(user);
  const rmOnlyNav =
    rmMenuAccess &&
    !user?.isProjectManager &&
    !hasAdminLikeAccess(user) &&
    !showCsgNav;

  return {
    rmMenuAccess,
    showCsgNav,
    pmAndCsg,
    dhRestricted,
    rmOnlyNav,
  };
}

/**
 * @param {object|null|undefined} user
 * @param {NavContext} ctx
 */
export function resolveMyTimesheetPath(user, ctx) {
  if (
    user?.isProjectManager ||
    shouldShowAdminPersonalTimesheetNav(user) ||
    ctx.rmOnlyNav
  ) {
    return '/pm/timesheet';
  }
  return '/employee';
}

/**
 * @param {object|null|undefined} user
 * @param {NavContext} ctx
 */
export function isMyTimesheetNavVisible(user, ctx) {
  if (!user) return false;
  if (user.isProjectManager) return true;
  if (shouldShowAdminPersonalTimesheetNav(user)) return true;
  if (ctx.rmOnlyNav) return true;
  if (!hasAdminLikeAccess(user)) return true;
  return false;
}

/**
 * @param {object|null|undefined} user
 * @param {NavContext} ctx
 * @returns {boolean}
 */
export function isReviewTimesheetsVisible(user, ctx) {
  return !!(user?.isProjectManager || ctx.rmOnlyNav);
}

/**
 * @param {object|null|undefined} user
 * @returns {string}
 */
export function resolveAuditLogPath(user) {
  return isAdmin(user) ? '/admin/audit-log' : '/account/audit-log';
}

/**
 * @typedef {Object} NavItemDef
 * @property {string} id
 * @property {string} label
 * @property {NavItemType} type
 * @property {string} [path]
 * @property {string} [actionId]
 * @property {string} icon
 * @property {string} iconColor
 * @property {(user: object, ctx: NavContext) => boolean} visible
 * @property {(user: object, ctx: NavContext) => string} [resolveLabel]
 * @property {string[]} [matchPaths]
 */

/**
 * @typedef {Object} NavSectionDef
 * @property {string} id
 * @property {string} title
 * @property {(user: object, ctx: NavContext) => string} [resolveSectionTitle]
 * @property {NavItemDef[]} items
 */

/** @type {NavSectionDef[]} */
export const NAV_SECTIONS = [
  {
    id: 'my-work',
    title: 'My Work',
    items: [
      {
        id: 'my-timesheet',
        label: 'My Timesheet',
        type: 'link',
        path: '/employee',
        icon: 'Calendar',
        iconColor: 'text-green-600 dark:text-green-400',
        visible: (user, ctx) => isMyTimesheetNavVisible(user, ctx),
        matchPaths: ['/employee', '/pm/timesheet'],
      },
      {
        id: 'my-skill-matrix',
        label: 'My Skills',
        type: 'link',
        path: '/skills/my-matrix',
        icon: 'Layers',
        iconColor: 'text-indigo-600 dark:text-indigo-400',
        visible: (user) => canAccessMySkillMatrix(user),
        matchPaths: ['/skills/my-matrix'],
        discovery: { badge: 'new' },
      },
      {
        id: 'my-audit-log',
        label: 'Audit Log',
        type: 'link',
        path: '/account/audit-log',
        icon: 'ScrollText',
        iconColor: 'text-slate-600 dark:text-slate-400',
        visible: (user) => !!user,
        matchPaths: ['/account/audit-log', '/admin/audit-log'],
        discovery: { badge: 'new', scope: 'auditLog' },
      },
    ],
  },
  {
    id: 'project-management',
    title: 'Project Management',
    resolveSectionTitle: (_user, ctx) => (ctx.rmOnlyNav ? 'Team' : 'Project Management'),
    items: [
      {
        id: 'pm-dashboard',
        label: 'PM Dashboard',
        type: 'link',
        path: '/pm/dashboard',
        icon: 'BarChart3',
        iconColor: 'text-purple-600 dark:text-purple-400',
        visible: (user) => !!user?.isProjectManager,
        matchPaths: ['/pm/dashboard'],
      },
      {
        id: 'review-timesheets',
        label: 'Review Timesheets',
        type: 'link',
        path: '/pm/review-timesheets',
        icon: 'CheckCircle',
        iconColor: 'text-purple-600 dark:text-purple-400',
        visible: (user, ctx) => isReviewTimesheetsVisible(user, ctx),
        matchPaths: ['/pm/review-timesheets'],
      },
      {
        id: 'timesheet-logs',
        label: 'Timesheet Logs',
        type: 'link',
        path: '/pm/timesheet-logs',
        icon: 'Eye',
        iconColor: 'text-purple-600 dark:text-purple-400',
        visible: (user, ctx) => isReviewTimesheetsVisible(user, ctx),
        matchPaths: ['/pm/timesheet-logs'],
      },
      {
        id: 'pm-download-timesheet',
        label: 'Download Timesheet',
        type: 'link',
        path: '/pm/approved-timesheet',
        icon: 'CheckCircle',
        iconColor: 'text-green-600 dark:text-green-400',
        visible: (user) => !!user?.isProjectManager,
        resolveLabel: (_user, ctx) =>
          ctx.pmAndCsg ? 'PM Timesheet Download' : 'Download Timesheet',
        matchPaths: ['/pm/approved-timesheet'],
      },
    ],
  },
  {
    id: 'csg-operations',
    title: 'CSG Operations',
    items: [
      {
        id: 'csg-project-hours',
        label: 'Project Hours View',
        type: 'link',
        path: '/csg/project-hours',
        icon: 'FolderOpen',
        iconColor: 'text-teal-600 dark:text-teal-400',
        visible: (_user, ctx) => ctx.showCsgNav,
        matchPaths: ['/csg/project-hours'],
      },
      {
        id: 'csg-employee-hours',
        label: 'Employee Hours View',
        type: 'link',
        path: '/csg/time-analysis',
        icon: 'BarChart3',
        iconColor: 'text-indigo-600 dark:text-indigo-400',
        visible: (_user, ctx) => ctx.showCsgNav,
        matchPaths: ['/csg/time-analysis'],
      },
      {
        id: 'csg-download-timesheet',
        label: 'Download Timesheet',
        type: 'link',
        path: '/csg/approved-timesheets',
        icon: 'CheckCircle',
        iconColor: 'text-green-600 dark:text-green-400',
        visible: (_user, ctx) => ctx.showCsgNav,
        resolveLabel: (_user, ctx) =>
          ctx.pmAndCsg ? 'CSG Timesheet Download' : 'Download Timesheet',
        matchPaths: ['/csg/approved-timesheets'],
      },
    ],
  },
  {
    id: 'administration',
    title: 'Administration',
    items: [
      {
        id: 'admin-dashboard',
        label: 'Admin Dashboard',
        type: 'link',
        path: '/admin/dashboard',
        icon: 'BarChart3',
        iconColor: 'text-purple-600 dark:text-purple-400',
        visible: (user) => hasAdminLikeAccess(user),
        resolveLabel: (_user, ctx) =>
          ctx.dhRestricted ? 'DH Dashboard' : 'Admin Dashboard',
        matchPaths: ['/admin/dashboard'],
      },
      {
        id: 'admin-notifications',
        label: 'Project Access Requests',
        type: 'link',
        path: '/admin/notifications',
        icon: 'Mail',
        iconColor: 'text-orange-600 dark:text-orange-400',
        visible: (user) => hasAdminLikeAccess(user),
        matchPaths: ['/admin/notifications'],
      },
      {
        id: 'reporting-managers',
        label: 'Reporting Managers',
        type: 'link',
        path: '/admin/reporting-managers',
        icon: 'UserCheck',
        iconColor: 'text-blue-600 dark:text-blue-400',
        visible: (user) => hasAdminLikeAccess(user),
        matchPaths: ['/admin/reporting-managers'],
      },
      {
        id: 'optional-timesheets',
        label: 'Optional Timesheets',
        type: 'link',
        path: '/admin/optional-timesheets',
        icon: 'ClipboardList',
        iconColor: 'text-cyan-600 dark:text-cyan-400',
        visible: (user, ctx) => hasAdminLikeAccess(user) && !ctx.dhRestricted,
        matchPaths: ['/admin/optional-timesheets'],
      },
      {
        id: 'admin-clients',
        label: 'Clients',
        type: 'link',
        path: '/admin/clients',
        icon: 'Building2',
        iconColor: 'text-green-600 dark:text-green-400',
        visible: (user, ctx) => hasAdminLikeAccess(user) && !ctx.dhRestricted,
        matchPaths: ['/admin/clients', '/admin/add-client'],
      },
      {
        id: 'admin-project-managers',
        label: 'Project Managers',
        type: 'link',
        path: '/admin/project-managers',
        icon: 'FolderOpen',
        iconColor: 'text-purple-600 dark:text-purple-400',
        visible: (user, ctx) => hasAdminLikeAccess(user) && !ctx.dhRestricted,
        matchPaths: ['/admin/project-managers'],
      },
      {
        id: 'admin-projects',
        label: 'Projects',
        type: 'link',
        path: '/admin/projects',
        icon: 'FolderOpen',
        iconColor: 'text-blue-600 dark:text-blue-400',
        visible: (user, ctx) => hasAdminLikeAccess(user) && !ctx.dhRestricted,
        matchPaths: ['/admin/projects'],
      },
      {
        id: 'admin-admins',
        label: 'Administrators',
        type: 'link',
        path: '/admin/admins',
        icon: 'Shield',
        iconColor: 'text-red-600 dark:text-red-400',
        visible: (user, ctx) => hasAdminLikeAccess(user) && !ctx.dhRestricted,
        matchPaths: ['/admin/admins'],
      },
      {
        id: 'admin-configuration',
        label: 'Configuration',
        type: 'link',
        path: '/admin/configuration',
        icon: 'Settings',
        iconColor: 'text-gray-600 dark:text-gray-300',
        visible: (user, ctx) => hasAdminLikeAccess(user) && !ctx.dhRestricted,
        matchPaths: ['/admin/configuration'],
      },
      {
        id: 'admin-holidays',
        label: 'Holiday Calendar',
        type: 'link',
        path: '/admin/holidays',
        icon: 'Calendar',
        iconColor: 'text-amber-600 dark:text-amber-400',
        visible: (user, ctx) => hasAdminLikeAccess(user) && !ctx.dhRestricted,
        matchPaths: ['/admin/holidays'],
      },
      {
        id: 'manage-skills',
        label: 'Manage Skills',
        type: 'link',
        path: '/admin/skills/manage',
        icon: 'Layers',
        iconColor: 'text-violet-600 dark:text-violet-400',
        visible: (user) => canManageSkillCatalog(user),
        matchPaths: ['/admin/skills/manage'],
        discovery: { badge: 'new' },
      },
    ],
  },
  {
    id: 'reports',
    title: 'Reports & Analytics',
    items: [
      {
        id: 'admin-employee-hours',
        label: 'Employee Hours View',
        type: 'link',
        path: '/admin/time-analysis',
        icon: 'BarChart3',
        iconColor: 'text-indigo-600 dark:text-indigo-400',
        visible: (user) => hasAdminLikeAccess(user),
        matchPaths: ['/admin/time-analysis', '/pm/time-analysis'],
      },
      {
        id: 'admin-project-hours',
        label: 'Project Hours View',
        type: 'link',
        path: '/admin/project-hours',
        icon: 'FolderOpen',
        iconColor: 'text-teal-600 dark:text-teal-400',
        visible: (user) => hasAdminLikeAccess(user),
        matchPaths: ['/admin/project-hours', '/pm/project-hours'],
      },
      {
        id: 'all-employee-report',
        label: 'All Employee Report',
        type: 'link',
        path: '/admin/employee-report',
        icon: 'FileText',
        iconColor: 'text-blue-600 dark:text-blue-400',
        visible: (user) => hasAdminLikeAccess(user),
        matchPaths: ['/admin/employee-report'],
      },
      {
        id: 'admin-download-timesheet',
        label: 'Download Timesheet',
        type: 'link',
        path: '/admin/approved-timesheets',
        icon: 'CheckCircle',
        iconColor: 'text-green-600 dark:text-green-400',
        visible: (user) => hasAdminLikeAccess(user),
        matchPaths: ['/admin/approved-timesheets'],
      },
      {
        id: 'survey-responses',
        label: 'Survey Responses',
        type: 'link',
        path: '/admin/survey-responses',
        icon: 'FileText',
        iconColor: 'text-indigo-600 dark:text-indigo-400',
        visible: (user) => hasAdminLikeAccess(user) && FEATURE_AI_SURVEY_UI_ENABLED,
        matchPaths: ['/admin/survey-responses', '/admin/survey-response'],
      },
    ],
  },
  {
    id: 'talent',
    title: 'Talent',
    items: [
      {
        id: 'resource-finder',
        label: 'Resource Finder',
        type: 'link',
        path: '/skills/resource-finder',
        icon: 'Layers',
        iconColor: 'text-indigo-600 dark:text-indigo-400',
        visible: (user) => canAccessResourceFinder(user),
        matchPaths: ['/skills/resource-finder'],
        discovery: { badge: 'new' },
      },
      {
        id: 'team-resumes',
        label: 'Team Resumes',
        type: 'link',
        path: '/account/team-resumes',
        icon: 'FileText',
        iconColor: 'text-sky-600 dark:text-sky-400',
        visible: (user) => canAccessTeamResumes(user),
        matchPaths: ['/account/team-resumes'],
        discovery: { badge: 'new' },
      },
      {
        id: 'bench-management',
        label: 'Bench Management',
        type: 'link',
        path: '/skills/bench-management',
        icon: 'Users',
        iconColor: 'text-teal-600 dark:text-teal-400',
        visible: (user) => canAccessBenchManagement(user),
        matchPaths: ['/skills/bench-management'],
        discovery: { badge: 'new' },
      },
      {
        id: 'bench-analysis',
        label: 'Bench Analysis',
        type: 'link',
        path: '/skills/bench-analysis',
        icon: 'BarChart3',
        iconColor: 'text-violet-600 dark:text-violet-400',
        visible: (user) => canAccessBenchAnalysis(user),
        matchPaths: ['/skills/bench-analysis'],
        discovery: { badge: 'new' },
      },
    ],
  },
  {
    id: 'help-account',
    title: 'Help & Account',
    resolveSectionTitle: (_user, ctx) => (ctx.rmOnlyNav ? 'Help' : 'Help & Account'),
    items: [
      {
        id: 'my-profile',
        label: 'My Profile',
        type: 'link',
        path: '/account/profile',
        icon: 'User',
        iconColor: 'text-blue-600 dark:text-blue-400',
        visible: (user) => !!user,
        matchPaths: ['/account/profile'],
      },
      {
        id: 'request-project-access',
        label: 'Request Project Access',
        type: 'action',
        actionId: 'request-project-access',
        icon: 'Mail',
        iconColor: 'text-gray-600 dark:text-gray-300',
        visible: (user, ctx) => !hasAdminLikeAccess(user) && !ctx.rmOnlyNav,
      },
      {
        id: 'faqs',
        label: 'FAQs',
        type: 'action',
        actionId: 'faqs',
        icon: 'HelpCircle',
        iconColor: 'text-gray-600 dark:text-gray-300',
        visible: () => true,
      },
      {
        id: 'app-tour',
        label: 'App Tour',
        type: 'action',
        actionId: 'app-tour',
        icon: 'Compass',
        iconColor: 'text-blue-600 dark:text-blue-400',
        visible: (user, ctx) => Boolean(ctx.walkthroughEnabled),
      },
      {
        id: 'ai-agent-survey',
        label: 'AI Agent Adoption Survey',
        type: 'link',
        path: '/employee/ai-agent-survey',
        icon: 'ClipboardList',
        iconColor: 'text-gray-600 dark:text-gray-300',
        visible: (user, ctx) =>
          !hasAdminLikeAccess(user) && !ctx.rmOnlyNav && FEATURE_AI_SURVEY_UI_ENABLED,
        matchPaths: ['/employee/ai-agent-survey'],
      },
      {
        id: 'sign-out',
        label: 'Sign Out',
        type: 'action',
        actionId: 'sign-out',
        icon: 'LogOut',
        iconColor: 'text-gray-600 dark:text-gray-300',
        visible: () => true,
      },
    ],
  },
];

export const SIDEBAR_ACTIONS = Object.freeze({
  REQUEST_PROJECT_ACCESS: 'request-project-access',
  FAQS: 'faqs',
  APP_TOUR: 'app-tour',
  SIGN_OUT: 'sign-out',
});
